import React, { useCallback, useState } from 'react'
import { Alert, Select, message } from '@surecompanies/core_components'
import dayjs from 'dayjs'

import { space } from './tokens'
import styles from './AdjustPaymentModal.module.scss'
import { TrashIcon } from './AdjustPaymentModal.icons'
import {
  extractApiErrorMessage,
  fmtMoney,
  isMoneyInput,
} from './AdjustPaymentModal.helpers'
import {
  buildRecordExplicitCreditRequest,
  EXPLICIT_CREDIT_REASON_LABELS,
  EXPLICIT_CREDIT_REASONS,
  normalizeExpectedAmount,
} from './recon-v2-helpers'
import {
  ExplicitCreditReason,
  PeriodDetailCredit,
  useDeleteExplicitCredit,
  useRecordExplicitCredit,
} from '../../api/reconciliation-v2-api-client'

/** Everything the entry row needs, owned by the modal rather than the section. */
export interface ExplicitCreditForm {
  amount: string
  setAmount: (value: string) => void
  reason: ExplicitCreditReason | null
  setReason: (value: ExplicitCreditReason) => void
  note: string
  setNote: (value: string) => void
  /** Marked only after a submit attempt — a pristine row shouldn't open red. */
  amountInvalid: boolean
  reasonInvalid: boolean
  isRecording: boolean
  isDeleting: boolean
  /** Rejection text for the Alerts, already extracted; null when there is none. */
  recordErrorMessage: string | null
  deleteErrorMessage: string | null
  /** Clears the entry row AND both rejections. */
  reset: () => void
  submit: () => Promise<void>
  remove: (creditId: string) => Promise<void>
}

/**
 * Entry-row state, both credit mutations and their handlers, as one value.
 *
 * Called by AdjustPaymentModal rather than by ExplicitCreditSection, because
 * the accordion unmounts a collapsed body: held inside the section, a
 * half-typed credit and a server rejection would both vanish on a mis-clicked
 * header. The modal's other write forms (`manualForm`, `setExpectedAmount`)
 * keep their state for exactly the same reason, so this one sits beside them
 * and the markup still lives in its own component.
 *
 * Resetting is the caller's to trigger — the modal already runs one effect per
 * open/cell change for the sibling forms, and {@link reset} joins it there.
 */
export const useExplicitCreditForm = (args: {
  /** Null/undefined outside cell mode — `submit` is then inert. */
  benefitId: string | null | undefined
  /** YYYY-MM-DD, first of the credited month. */
  periodStartDate: string | null | undefined
}): ExplicitCreditForm => {
  const recordExplicitCredit = useRecordExplicitCredit()
  const deleteExplicitCredit = useDeleteExplicitCredit()
  // `reason` starts unset so the operator has to choose one — the backend has
  // no default and the AC requires it to be marked.
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState<ExplicitCreditReason | null>(null)
  const [note, setNote] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const resetFields = useCallback(() => {
    setAmount('')
    setReason(null)
    setNote('')
    setSubmitted(false)
  }, [])
  const reset = useCallback(() => {
    resetFields()
    recordExplicitCredit.reset()
    deleteExplicitCredit.reset()
  }, [resetFields, recordExplicitCredit.reset, deleteExplicitCredit.reset])
  const request =
    args.benefitId && args.periodStartDate
      ? buildRecordExplicitCreditRequest({
          benefitId: args.benefitId,
          periodStartDate: args.periodStartDate,
          rawAmount: amount,
          reason,
          note,
        })
      : null
  // Blank, non-numeric and zero all fail the same way — the backend requires a
  // positive magnitude, so the field is simply marked.
  const amountNormalized = normalizeExpectedAmount(amount)
  const submit = async () => {
    setSubmitted(true)
    if (!request) return
    try {
      await recordExplicitCredit.mutateAsync(request)
      message.success(`Credited ${fmtMoney(Number(request.amount))}`)
      // Leave the section open with a cleared entry row — operators commonly
      // record several credits on one period in a row. The list itself refreshes
      // from the invalidated period-detail query, not from local state.
      resetFields()
    } catch {
      /* rejection shown via the Alert in the section */
    }
  }
  const remove = async (creditId: string) => {
    try {
      await deleteExplicitCredit.mutateAsync({ creditId })
      message.success('Credit removed')
    } catch {
      /* rejection shown via the Alert in the section */
    }
  }
  return {
    amount,
    setAmount,
    reason,
    setReason,
    note,
    setNote,
    amountInvalid:
      submitted && (amountNormalized === null || Number(amountNormalized) <= 0),
    reasonInvalid: submitted && reason === null,
    isRecording: recordExplicitCredit.isPending,
    isDeleting: deleteExplicitCredit.isPending,
    recordErrorMessage: recordExplicitCredit.isError
      ? extractApiErrorMessage(recordExplicitCredit.error)
      : null,
    deleteErrorMessage: deleteExplicitCredit.isError
      ? extractApiErrorMessage(deleteExplicitCredit.error)
      : null,
    reset,
    submit,
    remove,
  }
}

interface ExplicitCreditSectionProps {
  /** The period's credits, oldest-first, straight off the period detail. */
  credits: PeriodDetailCredit[]
  /** From {@link useExplicitCreditForm}, held by the modal so it survives collapse. */
  form: ExplicitCreditForm
  /**
   * Whether the period still has a live allocation. False suppresses the entry
   * row but NOT the list: EPS refuses a new credit on a period with no
   * allocation, while the credits already on it stand — the carrier charged
   * less regardless of which payment has landed.
   */
  hasLiveAllocations: boolean
  canWrite: boolean
}

/**
 * The "Explicit credit" accordion body: the period's existing credits, each
 * removable while unconsumed, over an entry row that clears itself and stays
 * ready for another.
 *
 * Presentational — every piece of state it touches comes from `form`. The list
 * is never held locally either: the mutations invalidate the period-detail
 * query, so `credits` arrives from the server after every submit and delete,
 * and the panel a few pixels away can never disagree with what is listed here.
 */
export const ExplicitCreditSection: React.FC<ExplicitCreditSectionProps> = ({
  credits,
  form,
  hasLiveAllocations,
  canWrite,
}) => {
  return (
    <>
      <div className={styles.creditIntro}>
        The carrier charged less than the expected price. Recording it here
        leaves the expected price — what the member was invoiced on — untouched
        and reduces what they still owe.
      </div>

      {credits.length > 0 ? (
        <ul className={styles.creditList}>
          {credits.map((credit) => (
            <li className={styles.creditRow} key={credit.id}>
              <span className={styles.creditAmount}>
                −{fmtMoney(credit.amount)}
              </span>
              <div className={styles.creditMeta}>
                <span className={styles.creditReason}>
                  {/* Never fall back to the wire spelling on screen (ADR 0001):
                      a reason the client doesn't know yet reads as the Other
                      bucket it belongs to, not as CARRIER_REBATE. */}
                  {EXPLICIT_CREDIT_REASON_LABELS[credit.reason] ??
                    EXPLICIT_CREDIT_REASON_LABELS[ExplicitCreditReason.OTHER]}
                </span>
                <span className={styles.creditSubMeta}>
                  {[
                    credit.createdBy,
                    dayjs(credit.createdAt).isValid()
                      ? dayjs(credit.createdAt).format('MMM D, YYYY')
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {credit.note ? (
                  <span className={styles.creditNote}>{credit.note}</span>
                ) : null}
              </div>
              <button
                className={styles.creditRemoveBtn}
                type="button"
                title={
                  credit.consumed
                    ? 'This credit has been applied to an invoice and can no longer be removed'
                    : 'Remove this credit'
                }
                aria-label="Remove credit"
                disabled={credit.consumed || form.isDeleting || !canWrite}
                onClick={() => form.remove(credit.id)}
              >
                <TrashIcon />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className={styles.creditEmpty}>
          No explicit credits on this benefit-month.
        </div>
      )}

      {!hasLiveAllocations ? (
        <div className={styles.creditEmpty}>
          This benefit-month has no live allocation, so no further credit can be
          recorded on it. The credits above still stand — the carrier charged
          less regardless of which payment has landed.
        </div>
      ) : (
        <div className={styles.formRow}>
          <div className={styles.formMoneyWrap}>
            <div className={styles.fieldLabel}>
              Amount<span className={styles.req}> *</span>
            </div>
            <input
              className={`${styles.formInput} ${styles.moneyInput}${
                form.amountInvalid ? ` ${styles.invalid}` : ''
              }`}
              value={form.amount}
              inputMode="decimal"
              placeholder="0.00"
              aria-invalid={form.amountInvalid}
              disabled={!canWrite}
              onChange={(e) => {
                const raw = e.target.value
                if (isMoneyInput(raw)) form.setAmount(raw)
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div className={styles.fieldLabel}>
              Reason<span className={styles.req}> *</span>
            </div>
            <Select
              value={form.reason ?? undefined}
              onChange={(v: ExplicitCreditReason) => form.setReason(v)}
              style={{ width: '100%' }}
              status={form.reasonInvalid ? 'error' : undefined}
              placeholder="Select a reason"
              disabled={!canWrite}
              options={EXPLICIT_CREDIT_REASONS.map((r) => ({
                value: r,
                label: EXPLICIT_CREDIT_REASON_LABELS[r],
              }))}
            />
          </div>
          <div style={{ flex: 2, minWidth: 180 }}>
            <div className={styles.fieldLabel}>
              Note <span className={styles.optionalMark}>(optional)</span>
            </div>
            <input
              className={styles.formInput}
              value={form.note}
              placeholder="Rebate reference, carrier correspondence id…"
              title="Carrier and billing context only — no member names or other identifiers."
              disabled={!canWrite}
              onChange={(e) => form.setNote(e.target.value)}
            />
          </div>
          <div className={styles.formActions}>
            <button
              className={styles.cancelBtn}
              type="button"
              disabled={form.isRecording}
              // `reset` clears the rejection Alerts too — abandoning the entry
              // should leave no stale error behind. (A successful submit clears
              // them on its own via the mutation's state transition.)
              onClick={form.reset}
            >
              Cancel
            </button>
            <button
              className={`${styles.formSubmitBtn}${
                form.isRecording ? ` ${styles.pending}` : ''
              }`}
              type="button"
              disabled={form.isRecording || !canWrite}
              onClick={form.submit}
            >
              {form.isRecording ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {form.recordErrorMessage ? (
        <Alert
          type="error"
          message={`Failed to record credit: ${form.recordErrorMessage}`}
          style={{ marginTop: space.md }}
        />
      ) : null}
      {form.deleteErrorMessage ? (
        <Alert
          type="error"
          message={`Failed to remove credit: ${form.deleteErrorMessage}`}
          style={{ marginTop: space.md }}
        />
      ) : null}
    </>
  )
}
